/**
 * ItemGo Client-Side Error Logger
 *
 * Captures runtime errors, unhandled rejections, console.errors,
 * and console.warns from production and stores them in Firestore.
 *
 * IMPORTANT: Must be initialized at the VERY TOP of main.tsx,
 * before any other initialization code.
 */
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config.js";
const ERROR_COLLECTION = "clientErrors";
const RATE_LIMIT_MS = 1_000; // 1 second dedup per message
const recentErrors = new Map();
const IGNORED_CONSOLE_PATTERNS = [
    "Unable to load preload script",
    "preload-browserView.js",
    "[getThemeColors]",
];
function isProductionNow() {
    try {
        return !import.meta.env?.DEV;
    }
    catch {
        return true;
    }
}
function shouldSend(key) {
    const last = recentErrors.get(key);
    const now = Date.now();
    if (last && now - last < RATE_LIMIT_MS)
        return false;
    recentErrors.set(key, now);
    if (recentErrors.size > 200) {
        const oldest = now - 60_000;
        for (const [k, t] of recentErrors) {
            if (t < oldest)
                recentErrors.delete(k);
        }
    }
    return true;
}
function formatConsoleMessage(args) {
    return args
        .map((a) => {
        if (typeof a !== "object" || a == null)
            return String(a);
        if (typeof a.message === "string" && a.message.length > 0)
            return a.message;
        try {
            return JSON.stringify(a).slice(0, 200);
        }
        catch {
            return "[unserializable object]";
        }
    })
        .join(" ");
}
function shouldIgnoreConsoleMessage(message) {
    return IGNORED_CONSOLE_PATTERNS.some((pattern) => message.includes(pattern));
}
// Queue to batch-send errors
void (0); // placeholder for future batching
async function sendToFirestore(event) {
    try {
        const key = `${event.type}:${(event.message || "").slice(0, 100)}`;
        if (!shouldSend(key))
            return;
        await addDoc(collection(db, ERROR_COLLECTION), {
            ...event,
            timestamp: serverTimestamp(),
        });
    }
    catch {
        // Fail silently
    }
}
function logError(event) {
    // Always capture — the handler hooks are set up immediately
    const fullEvent = {
        ...event,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
        url_location: typeof window !== "undefined" ? window.location.href : "",
    };
    // Fire-and-forget — don't block
    sendToFirestore(fullEvent);
}
/**
 * Initialize the client-side error logger.
 * Call IMMEDIATELY at the top of main.tsx, before anything else.
 */
export function initErrorLogger() {
    if (!isProductionNow()) {
        console.log("[ErrorLogger] Dev mode — skipping capture");
        return;
    }
    console.log("[ErrorLogger] Initialized");
    // ═══ Window-level error handler ═══
    window.onerror = (message, source, lineno, colno, error) => {
        logError({
            type: "window.onerror",
            message: String(message),
            stack: error?.stack || undefined,
            url: source || undefined,
            line: lineno || undefined,
            col: colno || undefined,
        });
    };
    // ═══ Unhandled promise rejections ═══
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        logError({
            type: "unhandledrejection",
            message: reason?.message || String(reason),
            stack: reason?.stack || undefined,
        });
    });
    // ═══ Override console.error ═══
    const origError = console.error;
    console.error = function (...args) {
        const msg = formatConsoleMessage(args);
        if (shouldIgnoreConsoleMessage(msg))
            return;
        origError.apply(console, args);
        logError({
            type: "console.error",
            message: msg.slice(0, 500),
            stack: new Error().stack?.split("\n").slice(2).join("\n") || undefined,
        });
    };
    // ═══ Override console.warn (many React issues logged as warn) ═══
    const origWarn = console.warn;
    console.warn = function (...args) {
        const msg = formatConsoleMessage(args);
        if (shouldIgnoreConsoleMessage(msg))
            return;
        origWarn.apply(console, args);
        logError({
            type: "console.warn",
            message: msg.slice(0, 500),
            stack: new Error().stack?.split("\n").slice(2).join("\n") || undefined,
        });
    };
    // ═══ Flush pending errors before page unload ═══
    if (typeof window !== "undefined") {
        window.addEventListener("beforeunload", () => {
            // Errors that were queued will be sent synchronously
        });
    }
}
/**
 * Manually log an error (for try/catch blocks).
 */
export function logClientError(error, context) {
    logError({
        type: "console.error",
        message: context ? `[${context}] ${error.message}` : error.message,
        stack: error.stack || undefined,
    });
}
//# sourceMappingURL=client-errors.js.map